import { Flex, SystemProps, Text } from "flicket-ui";
import find from "lodash/find";
import { useContext, useEffect } from "react";
import styled from "styled-components";
import { Select } from "~components";
import { SeatsIOSeat } from "~components/reservation/constants";
import { ReservationContext, ReservationState } from "~context";
import { PricingHookResponse } from "~hooks";
import { formatSeatLocation, getCategoryDataFromLabel } from "~lib/helpers";
import PriceWithFee from "../../common/common.PriceWithFee";
import { ListItemDetails } from "./actions.item.ListItemDetails";

const StyledListItem = styled(Flex)<SystemProps>`
  margin-bottom: ${(p) => p.theme.space[3]}px;

  justify-content: space-between;
  align-items: center;
`;

interface ListItemProps {
  seat: SeatsIOSeat;
  options: ReservationState["options"];
  handleTicketTypeChange: (id: any, newType: any) => void;
  getPrice: PricingHookResponse["getPrice"];
  showHiddenFees?: boolean;
}

export const ListItem = ({
  seat,
  options,
  handleTicketTypeChange,
  getPrice,
  showHiddenFees = false,
}: ListItemProps) => {
  const { options: filterOptions, zones } = useContext(ReservationContext);

  useEffect(() => {
    const adult = find(options, (option) =>
      option.label.toLowerCase().includes("adult")
    );

    if (!seat?.ticketType) {
      handleTicketTypeChange(seat?.customId ?? seat.id, adult ?? options[0]);
    }
  }, [options, filterOptions, zones]);

  const { zone, section } = getCategoryDataFromLabel(seat?.category?.label);

  const price = getPrice(seat);

  return (
    <StyledListItem mb={{ _: "6/4", md: 3 }} key={seat?.uuid}>
      <Flex flexDir="column" mr={1}>
        <Text
          fontSize={3}
          lineHeight="normal"
          fontWeight="extraBold"
          color="N700"
        >
          {formatSeatLocation(zone, section, seat?.label)}
        </Text>
        <Text fontSize={2} lineHeight="normal" color="N600" mt="1/4">
          <PriceWithFee
            price={price as number}
            ticketType={seat.ticketType}
            showFees={showHiddenFees}
          />
        </Text>
        <ListItemDetails
          name={seat?.ticketType?.label}
          description={seat?.ticketType?.description}
          restrictions={seat?.ticketType?.restrictions}
          addonImage={seat?.ticketType?.addonImage}
          ticketImage={seat?.ticketType?.ticketImage}
        />
      </Flex>
      <Flex minW="96px">
        <Select
          defaultValue={options[0]}
          options={options}
          onChange={(value) =>
            handleTicketTypeChange(seat?.customId ?? seat.id, value)
          }
          value={seat.ticketType}
          small
        />
      </Flex>
    </StyledListItem>
  );
};
